<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useTempRecordStore, TempRecordType, TEMP_RECORD_KEYS } from '@/store/tempRecord'
import { getTopbarImageList } from 'holopix-api';
import { TopbarImageItem } from '@xmiles/holopix-types';
import { useUserStore } from '@/store/user'
import { useEventTracking } from '@/hooks/useEventTracking';
import { useRoute } from 'vue-router';


const route = useRoute()
const userStore = useUserStore()
const { isLogin, userInfo } = storeToRefs(userStore)
const tempRecordStore = useTempRecordStore()
const imageList = ref<TopbarImageItem[]>([])

const fetchTopbarImages = async () => {
    try {
        const userId = isLogin.value && userInfo.value.id ? userInfo.value.id : undefined
        imageList.value = await getTopbarImageList(userId)
    } catch (error) {
        console.error('获取 topbar 配置失败:', error)
        imageList.value = []
    }
}


/**
 * 是否显示顶部栏
 */
const isShowTopbar = computed(() => {
    // 检查是否被关闭过
    const isClosed = tempRecordStore.hasRecord(TEMP_RECORD_KEYS.TOPBAR_CLOSED)
    // images 不能为空
    const hasImages = images.value.length > 0

    return !isClosed && hasImages;
});

/**
 * 轮播图
 */
const images = computed(() => {
    return imageList.value
        .sort((a, b) => a.sort - b.sort)
        .map(item => {
            return {
                id: item.id,
                title: item.title,
                image: item.picture,
                link: item.link || 'javascript:void(0)',
                target: item.link ? '_blank' : '_self',
            }
        })
})

/**
 * 关闭顶部栏
 */
const handleClickClose = (e: MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    // 记录关闭状态，在当前天重置生效
    tempRecordStore.setRecord(TEMP_RECORD_KEYS.TOPBAR_CLOSED, true, TempRecordType.DAILY_RESET)
}

/**
 * 点击轮播图项
 */
const handleClickItem = (item: any) => {
    useEventTracking({
        event: 'click',
        eventValue: {
            routeName: route.name as string,
            button: 'topbar_strip',
            stripTitle: item.title,
        }
    })
}


// 未登录与登录后 userScope 不同；登录态或 userId 变化时重新拉取
watch(
    () => (isLogin.value ? userInfo.value.id : ''),
    () => {
        fetchTopbarImages()
    },
    { immediate: true }
)

</script>

<template>
    <div class="top-bar" v-if="isShowTopbar">
        <div class="top-bar-content">
            <a-carousel :autoplay="true" :loop="true" :speed="500" :dots="false" :arrows="false" :autoplaySpeed="5000">
                <div v-for="item in images" :key="item.id" class="carousel-item" @click="handleClickItem(item)">
                    <a :href="item.link" :target="item.target">
                        <img :src="item.image" :alt="item.title" />
                    </a>
                </div>
            </a-carousel>
        </div>

        <div class="top-bar-close" @click="handleClickClose">
            <IconFont name="icon-guanbi" />
        </div>
    </div>
</template>

<style lang="less" scoped>
.top-bar {
    position: relative;

    .top-bar-content {
        width: 100%;
        height: 58px;
    }

    .top-bar-close {
        position: absolute;
        top: 12px;
        right: 12px;
        cursor: pointer;
        width: 34px;
        height: 34px;
        border-radius: 12px;
        border: 1px solid rgba(60, 60, 67, 0.18);
        background: rgba(60, 60, 67, 0.18);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .carousel-item {
        width: 100%;
        height: 58px;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center center;
        }
    }
}
</style>